import React, { useEffect, useState } from 'react';
import { apiGet } from '../../api';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';

function getDefaultDates() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

const UserIncome = () => {
  const { username } = useParams();
  const [income, setIncome] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ ...getDefaultDates(), status: 'all', order: 'desc' });
  const [useDates, setUseDates] = useState(false);

  const fetchIncome = async (pageNum = 1, filterVals = filters) => {
    setLoading(true);
    try {
      let query = `/api/admin/user/${username}/income?page=${pageNum}&pageSize=15`;
      if (filterVals.status && filterVals.status !== 'all') {
        query += `&status=${filterVals.status}`;
      }
      if (filterVals.order) {
        query += `&order=${filterVals.order}`;
      }
      if (useDates) {
        query += `&startDate=${filterVals.startDate}&endDate=${filterVals.endDate}`;
      }
      const res = await apiGet(query, {});
      setIncome(res.data.data.data);
      setTotalPages(res.data.data.totalPages);
    } catch (err) {
      toast.error('Failed to fetch income history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIncome(page, filters);
    // eslint-disable-next-line
  }, [page, filters, useDates, username]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPage(1);
  };

  const handleDateToggle = (e) => {
    setUseDates(e.target.checked);
    setPage(1);
  };

  const inputClass = "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";
  const labelClass = "block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5";

  return (
    <div>
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Management
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground mb-1">Income <span className="text-gradient-ginox">History</span></h1>
      <p className="text-sm text-muted-foreground mb-6">
        For <span className="font-mono text-foreground">#{username}</span>
      </p>

      {/* Filters */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className={labelClass}>Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className={inputClass}
              style={{ width: 'auto', minWidth: '120px' }}
            >
              <option value="all">All</option>
              <option value="released">Released</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Order</label>
            <select
              name="order"
              value={filters.order}
              onChange={handleFilterChange}
              className={inputClass}
              style={{ width: 'auto', minWidth: '140px' }}
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
          <div className="flex items-center gap-2 pb-0.5">
            <input
              type="checkbox"
              id="useDates"
              checked={useDates}
              onChange={handleDateToggle}
              className="h-4 w-4 rounded border-border/70 accent-ginox-green"
            />
            <label htmlFor="useDates" className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground cursor-pointer">
              Filter by Date
            </label>
          </div>
          {useDates && (
            <>
              <div>
                <label className={labelClass}>Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  max={filters.endDate}
                  className={inputClass}
                  style={{ width: 'auto' }}
                />
              </div>
              <div>
                <label className={labelClass}>End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  min={filters.startDate}
                  max={new Date().toISOString().slice(0, 10)}
                  className={inputClass}
                  style={{ width: 'auto' }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {['Tranx ID', 'Amount', 'Wallet', 'Type', 'Released', 'Date'].map(col => (
                <th key={col} className="border-b border-border/70 px-5 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-[#9aa1ac] whitespace-nowrap">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green mx-auto" />
                </td>
              </tr>
            ) : income.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">
                  No income found
                </td>
              </tr>
            ) : (
              income.map(i => (
                <tr key={i._id} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{i.tranx_id}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{i.amount}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{i.wallet_type}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{i.type}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      i.is_released
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                    }`}>
                      {i.is_released ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                    {new Date(i.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border/40">
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-md border border-border/70 bg-white/[0.02] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40"
            >
              Prev
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="rounded-md border border-border/70 bg-white/[0.02] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserIncome;
